var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, FormControl, Popover, Tooltip, Typography, } from '@mui/material';
import React, { useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import { isNumber } from 'underscore';
import { ArgbHexColorSpec, } from '../../parsing/UiTypesSpec';
import { argbHexColorToRGBColor, colorHexToRgbaCss, } from '../../utils/Color';
var rGBColorToArgbHex = function (color) {
    var alpha = color.a;
    if (isNumber(alpha) && (!Number.isInteger(alpha) || alpha === 1)) {
        alpha = Math.round(alpha * 255);
    }
    return "#".concat((alpha === null || alpha === void 0 ? void 0 : alpha.toString(16).padStart(2, '0')) || '00').concat(color.r.toString(16).padStart(2, '0')).concat(color.g.toString(16).padStart(2, '0')).concat(color.b.toString(16).padStart(2, '0'));
};
var Swatch = function (_a) {
    var color = _a.color;
    var canvasRef = React.useRef(null);
    React.useEffect(function () {
        var canvas = canvasRef.current;
        if (!canvas)
            return;
        var ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        // Create checkerboard pattern
        var size = 10;
        if (color === null) {
            // clear the canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        if (color !== null) {
            for (var x = 0; x < canvas.width; x += size) {
                for (var y = 0; y < canvas.height; y += size) {
                    ctx.fillStyle = ((x + y) / size) % 2 ? '#CCCCCC' : '#FFFFFF';
                    ctx.fillRect(x, y, size, size);
                }
            }
        }
        // Draw the actual color on top if set
        if (color !== '#00000000') {
            var rgba = colorHexToRgbaCss(color !== null && color !== void 0 ? color : undefined);
            if (rgba) {
                ctx.fillStyle = rgba;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
        }
    }, [color]);
    return (_jsxs("div", __assign({ style: { position: 'relative', display: 'inline-block' } }, { children: [_jsx("canvas", { ref: canvasRef, width: 50, height: 30, style: {
                    borderRadius: '4px',
                    border: '1px solid #564e43',
                    display: 'block',
                } }), color === null && (_jsx("span", __assign({ style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    fontFamily: 'RuneScape',
                } }, { children: "Unset" })))] })));
};
var ColorPicker = function (_a) {
    var _b;
    var color = _a.color, onChange = _a.onChange, disabled = _a.disabled;
    var _c = useState(null), anchorEl = _c[0], setAnchorEl = _c[1];
    var open = Boolean(anchorEl);
    var colorOrError = ArgbHexColorSpec.safeParse(color);
    var handleClick = function (event) {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };
    var handleClose = function () {
        setAnchorEl(null);
    };
    var handleChange = function (newColor) {
        var argbColor = rGBColorToArgbHex(newColor);
        setInputTextState(argbColor);
        onChange(argbColor);
        setInputTextState(argbColor);
    };
    var _d = useState(colorOrError.success ? colorOrError.data : '#FF000000'), inputTextState = _d[0], setInputTextState = _d[1];
    var handleHexChange = function (newColor) {
        var newColorOrError = ArgbHexColorSpec.safeParse(newColor);
        if (newColorOrError.success) {
            setInputTextState(newColorOrError.data);
            onChange(newColorOrError.data);
        }
    };
    var unset = color === undefined || color === '00000000';
    return (_jsxs(_Fragment, { children: [_jsx(Tooltip, __assign({ title: disabled
                    ? 'Color picker is disabled'
                    : !unset
                        ? 'Shift + Click to revert to default color'
                        : 'Click to pick a color' }, { children: _jsx("div", __assign({ style: {
                        padding: '5px',
                        display: 'inline-block',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    }, onClick: function (e) {
                        if (e.shiftKey) {
                            onChange(undefined);
                            setAnchorEl(null);
                        }
                        else {
                            handleClick(e);
                        }
                    } }, { children: _jsx(Swatch, { color: color }) })) })), _jsx(Popover, __assign({ open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                } }, { children: _jsxs("div", __assign({ style: {
                        backgroundColor: '#fff',
                        padding: '16px',
                        borderRadius: '8px',
                    } }, { children: [_jsx(RgbaColorPicker, { color: colorOrError.success
                                ? argbHexColorToRGBColor(color !== null && color !== void 0 ? color : undefined)
                                : {
                                    r: 0,
                                    g: 0,
                                    b: 0,
                                    a: 1,
                                }, onChange: handleChange }), _jsx("input", { type: "text", value: inputTextState, style: { fontFamily: 'monospace' }, onChange: function (e) { return handleHexChange(e.target.value); } }), _jsxs("div", __assign({ style: { fontSize: '12px', color: '#000' } }, { children: [_jsx("span", __assign({ style: { fontFamily: 'monospace' } }, { children: "#AARRGGBB" })), _jsx(Button, __assign({ variant: "text", size: "small", onClick: function () {
                                        onChange(undefined);
                                        setAnchorEl(null);
                                    } }, { children: "Reset to Default" }))] })), _jsx("div", { children: _jsx(Typography, __assign({ fontSize: "12px", color: "red" }, { children: (_b = colorOrError.error) === null || _b === void 0 ? void 0 : _b.message })) })] })) }))] }));
};
var ColorPickerInput = function (_a) {
    var _b, _c, _d, _e;
    var configField = _a.configField, config = _a.config, themeConfig = _a.themeConfig, input = _a.input, onChange = _a.onChange, disabled = _a.disabled, helpText = _a.helpText;
    return (_jsxs(FormControl, __assign({ component: "div" }, { children: [_jsx(ColorPicker, { color: (_e = (_c = (_b = config === null || config === void 0 ? void 0 : config[configField]) !== null && _b !== void 0 ? _b : themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig[configField]) !== null && _c !== void 0 ? _c : (_d = input === null || input === void 0 ? void 0 : input.default) === null || _d === void 0 ? void 0 : _d[configField]) !== null && _e !== void 0 ? _e : null, onChange: function (color) {
                    var _a;
                    onChange((_a = {}, _a[configField] = color, _a));
                }, disabled: disabled || false }), helpText && (_jsx(Typography, __assign({ sx: {
                    color: 'red',
                    fontFamily: 'RuneScape',
                    fontSize: '20px',
                    marginTop: '2px',
                    whiteSpace: 'pre-wrap',
                } }, { children: helpText })))] })));
};
export { ColorPicker, ColorPickerInput };
